/* eslint-disable no-restricted-syntax,guard-for-in */
import {
  Box,
  MenuItem,
  Pagination as MuiPagination,
  PaginationItem as MuiPaginationItem,
  Typography,
  useTheme,
} from '@mui/material';
import InventoryCarListItem, { GRAPHQL_REQUIRED_PROPS } from '@components/vehiclefleet/InventoryCarListItem';
import { gql } from '@apollo/client';
import { queryArguments, queryFields, queryVariables } from '@components/vehiclefleet/SearchAndFilterBar/graphQl';
import Container from '@components/atoms/Container';
import DefaultTemplate from '@components/templates/DefaultTemplate';
import { FiSearch } from 'react-icons/fi';
import Head from 'next/head';
import PageInsetTop from '@components/atoms/PageInsetTop';
import SearchAndFilterBar from '@components/vehiclefleet/SearchAndFilterBar';
import Banner from '@components/molecules/Banner';
import Select from '@components/atoms/Select';
import Hidden from '@components/atoms/Hidden';
import defaults from 'lodash/defaults';
import { getCarSearchRoute } from '@lib/routing';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { FILTER_SKELETON } from '@plugins/vehiclefleetapp';
import omitBy from 'lodash/omitBy';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { getAppData } from '@lib/appInitialState';
import Error from 'next/error';

export default function Index(props) {
  const {
    appData,
    items,
    filters,
    totalCount,
    isPurchasingEnabled,
  } = props;
  const theme = useTheme();
  const router = useRouter();
  const {
    t,
    i18n,
  } = useTranslation();
  const carListId = 'car-list';
  const currentPage = Math.floor(filters.skip / DEFAULT_ITEMS_PAGE_SIZE) + 1;
  const totalPages = Math.ceil(totalCount / DEFAULT_ITEMS_PAGE_SIZE);

  const handleSetFilterConfiguration = (options = {}) => {
    const query = defaults(options, filters);
    // eslint-disable-next-line no-underscore-dangle
    delete query.__typename;
    setFilterParams(query, router, i18n);
  };
  const handleGoToPage = (event, page) => {
    event.preventDefault();
    event.stopPropagation();

    const skip = (Math.max(page - 1, 0)) * DEFAULT_ITEMS_PAGE_SIZE;
    const take = DEFAULT_ITEMS_PAGE_SIZE;

    handleSetFilterConfiguration({
      skip,
      take,
    });
  };
  const handleChangeSort = (event) => {
    const sort = event.target.value;
    handleSetFilterConfiguration({
      sort,
    });
  };
  const handleChangeFilter = (field, params) => {
    const where = { skip: 0 };

    for (const key in params) {
      FILTER_SKELETON[key].addWhere(where, params[key], params);
    }

    if (where.vehicle_category) {
      delete where.vehicle_category;
    }

    handleSetFilterConfiguration(where);
  };
  const handleResetFilter = () => {
    const resetParams = getFilterParams({});
    setFilterParams(resetParams, router, i18n);
  };
  const getPagingUrl = (page) => {
    const skip = (Math.max(page - 1, 0)) * DEFAULT_ITEMS_PAGE_SIZE;
    const take = DEFAULT_ITEMS_PAGE_SIZE;
    const query = {
      ...filters,
      skip,
      take,
    };
    // eslint-disable-next-line no-underscore-dangle
    delete query.__typename;

    const url = getUrl(query, i18n);
    return url;
  };

  const pageTitle = t('pages.vehiclefleetapp.home.pageTitle', {
    currentPage,
    totalPages,
  });

  if (!router.asPath.startsWith(getCarSearchRoute(router.locale))) {
    return (
      <Error statusCode={404} title="Inhalt nicht gefunden" />
    );
  }

  const hasFilters = router.asPath.includes('?');

  return (
    <>
      <Head>
        <title>
          {pageTitle}
        </title>
        <meta
          content={t('pages.vehiclefleetapp.home.metaDescription', {
            currentPage,
            totalPages,
          })}
          name="description"
        />
        <meta content={pageTitle} property="og:title" />
        {hasFilters && (
          <meta content="noindex, follow" name="robots" />
        )}
      </Head>
      <DefaultTemplate>
        <Box
          sx={{
            backgroundColor: theme.palette.text.disabled,
            paddingBottom: theme.spacing(5),
            marginBottom: theme.spacing(4),
          }}
        >
          <PageInsetTop />
          <Container>
            <Box mb={3}>
              <Typography variant="h1">
                {t('pages.vehiclefleetapp.home.vehicleSearch')}
              </Typography>
            </Box>
            <Box>
              <SearchAndFilterBar
                filters={filters}
                id={carListId}
                onChange={handleChangeFilter}
                onReset={handleResetFilter}
              />
            </Box>
          </Container>
        </Box>
        <Container>
          <Banner appData={appData} />
          <Pagination
            getPagingUrl={getPagingUrl}
            hidePagination
            onChangePaging={handleGoToPage}
            onChangeSort={handleChangeSort}
            skip={filters.skip}
            sort={filters.sort}
            t={t}
            take={filters.take}
            totalCount={totalCount}
          />
          <Items
            id={carListId}
            isPurchasingEnabled={isPurchasingEnabled}
            items={items}
            t={t}
          />
          <Pagination
            getPagingUrl={getPagingUrl}
            onChangePaging={handleGoToPage}
            onChangeSort={handleChangeSort}
            onlyPagination
            skip={filters.skip}
            sort={filters.sort}
            t={t}
            take={filters.take}
            totalCount={totalCount}
          />
        </Container>
      </DefaultTemplate>
    </>
  );
}

export function Items({
  t,
  id,
  loading,
  items,
  isPurchasingEnabled,
}) {
  const theme = useTheme();
  if (loading) {
    return (
      <Box
        sx={{
          marginTop: theme.spacing(5),
          marginBottom: theme.spacing(5),
          display: 'grid',
          columnGap: `${theme.spacing(columnGap)}`,
          rowGap: `${theme.spacing(columnGap)}`,
          gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
          [theme.breakpoints.up('md')]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          },
          [theme.breakpoints.up('lg')]: {
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        <InventoryCarListItem useSkeleton />
        <InventoryCarListItem useSkeleton />
        <InventoryCarListItem useSkeleton />
      </Box>
    );
  }

  if (!items || items.length === 0) {
    return (
      <Box alignItems="center" display="flex" justifyContent="center" py={10}>
        <FiSearch
          style={{
            marginRight: theme.spacing(1),
            opacity: 0.5,
          }}
        />
        <Typography component="span" variant="body2">
          {t('pages.vehiclefleetapp.home.noVehiclesFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      id={id}
      sx={{
        marginTop: theme.spacing(5),
        marginBottom: theme.spacing(5),
        display: 'grid',
        columnGap: `${theme.spacing(columnGap)}`,
        rowGap: `${theme.spacing(columnGap)}`,
        gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
        [theme.breakpoints.up('md')]: {
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [theme.breakpoints.up('lg')]: {
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        },
      }}
    >
      {items.map((item) => (
        <InventoryCarListItem
          key={item.id}
          isPurchasingEnabled={isPurchasingEnabled}
          item={item}
        />
      ))}
    </Box>
  );
}

export function Pagination(props) {
  const {
    t,
    hidePagination,
    onlyPagination,
    sort,
    skip,
    take,
    totalCount,
    getPagingUrl,
    onChangePaging,
    onChangeSort,
  } = props;
  const page = Math.ceil(skip / take) + 1;
  const count = Math.ceil(totalCount / take);

  const renderPaginationItem = (item) => (
    <MuiPaginationItem
      component="a"
      href={getPagingUrl(item.page)}
      {...item}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {!hidePagination
        ? (
          <Box>
            <Hidden dir="up" size="md">
              <MuiPagination
                count={count}
                onChange={onChangePaging}
                page={page}
                renderItem={renderPaginationItem}
                siblingCount={0}
              />
            </Hidden>
            <Hidden dir="down" size="sm">
              <MuiPagination
                boundaryCount={1}
                count={count}
                onChange={onChangePaging}
                page={page}
                renderItem={renderPaginationItem}
                siblingCount={1}
              />
            </Hidden>
          </Box>
        )
        : (
          <div />
        )}
      {!onlyPagination && (
        <Box alignItems="center" display="flex">
          <Box mr={3}>
            <Typography
              sx={{
                display: 'inline-block',
                minWidth: '125px',
              }}
              variant="button"
            >
              {t('pages.vehiclefleetapp.home.sortBy')}
            </Typography>
          </Box>
          <Select
            hideLabel
            label="sort"
            onChange={onChangeSort}
            value={sort}
            variant="outlined"
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
    </Box>
  );
}

const columnGap = 7;

export async function getServerSideProps(ctx) {
  const {
    query,
    res,
    locale,
    resolvedUrl,
  } = ctx;
  const [graphQlClient, appData] = await getAppData(ctx);
  const filterOptions = getFilterParams(query);
  const {
    data,
    error,
  } = await graphQlClient.query({
    query: FETCH_ITEMS,
    variables: {
      ...filterOptions,
    },
  });

  if (error) {
    throw error;
  }

  if (res && data.cacheState.isEnabled) {
    // 10 min
    res.setHeader('Cache-Control', 'public, max-age=600');
  }

  return {
    props: {
      ...appData,
      isGlobalNoticeHidden: true,
      items: data?.carSearchItems?.items ?? [],
      filters: data?.carSearchItems?.filter ?? filterOptions,
      totalCount: data?.carSearchItems?.totalCount ?? 0,
      isPurchasingEnabled: Boolean(data?.carSearchConfig?.isPurchasingEnabled),
      suppressCanonical: (resolvedUrl ?? '').includes('?'),
    },
  };
}

function getFilterParams({
  skip,
  take,
  sort,
  ...query
}) {
  skip = Math.max(parseInt(skip ?? 0, 10), 0);
  take = Math.max(parseInt(take ?? DEFAULT_ITEMS_PAGE_SIZE, 10), DEFAULT_ITEMS_PAGE_SIZE);

  if (Number.isNaN(skip)) skip = 0;
  if (Number.isNaN(take)) take = DEFAULT_ITEMS_PAGE_SIZE;

  sort = sort ?? SORT_OPTIONS[0].value;
  const params = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const key in FILTER_SKELETON) {
    if (isNil(query[key]) || isEmpty(query[key])) {
      continue;
    }
    switch (FILTER_SKELETON[key].type) {
      case 'Boolean':
        params[key] = query[key].toLowerCase() === 'true';
        break;
      case 'String':
      case 'ID':
        params[key] = query[key];
        break;
      case 'Int': {
        const value = parseInt(query[key], 10);
        if (!Number.isNaN(value)) {
          params[key] = value;
        }
      }
        break;
      case '[Int]': {
        const values = query[key].split(',');
        params[key] = values
          .map((value) => parseInt(value, 10))
          .filter((value) => !Number.isNaN(value));
      }
        break;
      case '[String]':
        params[key] = query[key].split(',');
        break;
      default:
        console.warn(`Query param of type '${FILTER_SKELETON[key].type}' not supported.`);
        break;
    }
  }

  // Do not show dummy ads in the car search
  params.dummy = false;

  return {
    skip,
    take,
    sort,
    ...params,
  };
}

function getUrl(query, i18n) {
  const queryParams = new URLSearchParams(omitBy(query, isNil)).toString();
  return getCarSearchRoute(i18n.language, queryParams);
}

function setFilterParams(query, router, i18n) {
  const url = getUrl(query, i18n);

  window.scrollTo(0, 0);
  router.push(url);
}

export const DEFAULT_ITEMS_PAGE_SIZE = 12;

export const SORT_OPTIONS = [
  {
    value: 'price:asc,is_immediately_available:desc',
    label: 'Preis aufsteigend',
  },
  {
    value: 'price:desc',
    label: 'Preis absteigend',
  },
];

export const FETCH_ITEMS = gql`
  query GetInventoryCars($sort: String!, $skip: Int!, $take: Int!, ${queryVariables.join(', ')}) {
    carSearchItems(sort: $sort, skip: $skip, take: $take, ${queryArguments.join(', ')}) {
      totalCount
      items {
        ${GRAPHQL_REQUIRED_PROPS}
      }
      filter {
        skip
        take
        sort
        ${queryFields.join('\n')}
      }
    }
    carSearchConfig {
      data {
        id
        attributes {
          isPurchasingEnabled
        }
      }
    }
    cacheState {
      isEnabled
    }
  }
`;
